import { useEffect, useState } from "react";
import { Loader2, Save, X, CalendarOff, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";

const dayNames = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
// map display order (Mon..Sun) to python weekday keys "0".."6"
const dayKeys = ["0", "1", "2", "3", "4", "5", "6"];

const toISO = (d) => {
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};
const months = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
const prettyDate = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

export default function ScheduleManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hours, setHours] = useState({});
  const [blocked, setBlocked] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [pickDate, setPickDate] = useState(null);

  useEffect(() => {
    api.get("/admin/schedule").then((r) => {
      setHours(r.data.hours);
      setBlocked(r.data.blocked_dates);
      setCandidates(r.data.candidate_times);
      setLoading(false);
    });
  }, []);

  const toggle = (key, time) => {
    const cur = new Set(hours[key] || []);
    cur.has(time) ? cur.delete(time) : cur.add(time);
    setHours({ ...hours, [key]: candidates.filter((t) => cur.has(t)) });
  };

  const addBlocked = () => {
    if (!pickDate) return;
    const iso = toISO(pickDate);
    if (!blocked.includes(iso)) setBlocked([...blocked, iso].sort());
    setPickDate(null);
  };
  const removeBlocked = (iso) => setBlocked(blocked.filter((b) => b !== iso));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/schedule", { hours, blocked_dates: blocked });
      toast.success("Horários guardados.");
    } catch {
      toast.error("Não foi possível guardar.");
    }
    setSaving(false);
  };

  if (loading)
    return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-rose-700" /></div>;

  return (
    <div className="space-y-8" data-testid="schedule-manager">
      <div className="bg-white rounded-2xl border border-rose-100 p-6 lg:p-8">
        <h2 className="font-display text-2xl text-stone-900">Dias e horários</h2>
        <p className="text-sm text-stone-500 mt-1">Clique nas horas para as ativar ou desativar em cada dia.</p>
        <div className="mt-6 space-y-5">
          {dayKeys.map((key, i) => (
            <div key={key} className="grid md:grid-cols-[110px_1fr] gap-3 md:items-center">
              <span className="text-sm font-medium text-stone-700">{dayNames[i]}</span>
              <div className="flex flex-wrap gap-2">
                {candidates.map((t) => {
                  const active = (hours[key] || []).includes(t);
                  return (
                    <button
                      key={t}
                      data-testid={`slot-toggle-${key}-${t}`}
                      onClick={() => toggle(key, t)}
                      className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${
                        active ? "bg-rose-800 text-white border-rose-800" : "bg-white border-stone-200 text-stone-400 hover:border-rose-300"
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-rose-100 p-6 lg:p-8">
        <h2 className="font-display text-2xl text-stone-900 flex items-center gap-2">
          <CalendarOff className="w-5 h-5 text-rose-700" /> Folgas e feriados
        </h2>
        <p className="text-sm text-stone-500 mt-1">Adicione dias específicos em que não estará disponível.</p>
        <div className="mt-6 grid md:grid-cols-2 gap-8">
          <div className="rounded-2xl border border-rose-100 p-3 w-fit">
            <Calendar
              mode="single"
              selected={pickDate}
              onSelect={setPickDate}
              disabled={(d) => { const t = new Date(); t.setHours(0,0,0,0); return d < t; }}
            />
            <button onClick={addBlocked} disabled={!pickDate} data-testid="add-blocked-date"
              className="btn-rose mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-rose-800 text-white py-2.5 text-sm disabled:opacity-40">
              <Plus className="w-4 h-4" /> Adicionar folga
            </button>
          </div>
          <div>
            <h3 className="text-sm uppercase tracking-widest text-stone-400 mb-4">Folgas marcadas</h3>
            {blocked.length === 0 ? (
              <p className="text-sm text-stone-400">Nenhuma folga marcada.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {blocked.map((b) => (
                  <span key={b} data-testid={`blocked-${b}`}
                    className="inline-flex items-center gap-2 rounded-full bg-rose-50 border border-rose-100 text-rose-800 px-3 py-1.5 text-sm">
                    {prettyDate(b)}
                    <button onClick={() => removeBlocked(b)} className="hover:text-rose-950"><X className="w-3.5 h-3.5" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="save-schedule"
          className="btn-rose inline-flex items-center gap-2 rounded-full bg-rose-800 text-white px-8 py-3 text-sm disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar alterações
        </button>
      </div>
    </div>
  );
}
